/**
 * This is the core Utility function of the project
 * that allow to convert React component in Web Component
 * and attach style to it
 */
import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';

class CustomElement extends HTMLElement {
  constructor() {
    super();
    if (!this.constructor.shadowRoot) {
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, { attributes: true });
    }
  }

  connectedCallback() {
    this._innerHTML = this.innerHTML;
    this.mount();
  }

  disconnectedCallback() {
    this.unmount();
    if (!this.constructor.shadowRoot) {
      this.observer.disconnect();
    }
  }

  update() {
    this.unmount();
    this.mount();
  }

  mount() {
    const ReactComponent = this.constructor.ReactComponent;
    const isShadowRoot = this.constructor.shadowRoot;
    const root = isShadowRoot ? this.attachShadow({ mode: 'closed' }) : this;
    this.attachStyleSheetToShadowRoot(root);
    const componentProps = ReactComponent.defaultProps || {};
    const events = ReactComponent.defaultProps || {};
    const props = {
      ...this.getProps(this.attributes, componentProps),
      ...this.getEvents(events),
      children: this.parseHtmlToReact(this.innerHTML),
    };
    render(<ReactComponent {...props} />, root);
  }

  unmount() {
    unmountComponentAtNode(this);
  }

  attachStyleSheetToShadowRoot(root) {
    if (this.constructor.style && this.constructor.shadowRoot) {
      const sheet = new CSSStyleSheet();
      if (sheet && sheet.replaceSync) {
        // When TESTING "new CSSStyleSheet()" fails: JSDOM issue?
        sheet.replaceSync(this.constructor.style);
        root.adoptedStyleSheets = [sheet];
      }
    }
  }

  parseHtmlToReact(html) {
    return html; // && new htmlToReact.Parser().parse(html);
  }

  getProps(attributes, componentProps) {
    const _propTypes = componentProps || {};
    return [...attributes]
      .filter((attr) => attr.name !== 'style')
      .map((attr) => this.convert(_propTypes, attr.name, attr.value))
      .reduce((props, prop) => ({ ...props, [prop.name]: prop.value }), {});
  }

  getEvents(componentProps) {
    const _propTypes = componentProps || {};
    return Object.keys(_propTypes)
      .filter((key) => /on([A-Z].*)/.exec(key))
      .reduce(
        (events, ev) => ({
          ...events,
          [ev]: (...args) => this.dispatchEvent(new CustomEvent(ev, { detail: args, bubbles: true })),
        }),
        {}
      );
  }

  convert(propTypes, attrName, attrValue) {
    const propName = Object.keys(propTypes).find((key) => key.toLowerCase() === attrName);
    let value = attrValue;
    if (attrValue === 'true' || attrValue === 'false') {
      value = attrValue === 'true';
    } else if (!isNaN(attrValue) && attrValue !== '') {
      value = +attrValue;
    } else if (/^{.*}/.exec(attrValue)) {
      value = JSON.parse(attrValue);
    }
    return {
      name: propName || attrName,
      value,
    };
  }
}

/**
 * Creates a CustomElement
 * @param {ReactElement} ReactComponent
 * @param {string} style to apply to the component
 */
export function reactToWebComponent(ReactComponent, style = '', shadowRoot = false) {
  class CustomWebComponentElement extends CustomElement {}
  CustomWebComponentElement.style = style;
  CustomWebComponentElement.ReactComponent = ReactComponent;
  CustomWebComponentElement.shadowRoot = shadowRoot;
  return CustomWebComponentElement;
}
